import React from 'react';
import { Helmet } from 'react-helmet-async';

import { FeatureFlag } from 'src/shared/constants';
import { useLocation, useTranslate, useTreatment } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { isEnabled } from 'src/shared/telemetry/featureFlag';

const Favicon = () => {
  const useNewLogo = isEnabled(useTreatment(FeatureFlag.Web.BAUHAUS_LOGO));

  if (__DEV__) {
    return (
      <Helmet>
        <link
          href="https://assets.hover.to/favicons/hover_chartreuse_64.png"
          rel="icon"
        />
      </Helmet>
    );
  }

  if (useNewLogo) {
    return (
      <Helmet>
        <link
          href="https://assets.hover.to/favicons/hover_fuchsia_64.png"
          id="favicon-apple"
          rel="apple-touch-icon"
        />
        <link
          href="https://assets.hover.to/favicons/hover_fuchsia_16.ico"
          id="favicon-ico"
          rel="shortcut icon"
        />
        <link
          href="https://assets.hover.to/favicons/hover_fuchsia_16.png"
          id="favicon-16"
          rel="icon"
          sizes="16x16"
          type="image/png"
        />
        <link
          href="https://assets.hover.to/favicons/hover_fuchsia_32.png"
          id="favicon-32"
          rel="icon"
          sizes="32x32"
          type="image/png"
        />
        <link
          href="https://assets.hover.to/favicons/hover_fuchsia_64.png"
          id="favicon-64"
          rel="icon"
          sizes="64x64"
          type="image/png"
        />
      </Helmet>
    );
  }

  return (
    <Helmet>
      <link
        href="https://assets.hover.to/favicons/hover_fav_apple.png"
        id="favicon-apple"
        rel="apple-touch-icon"
      />
      <link
        href="https://assets.hover.to/favicons/hover_fav.ico"
        id="favicon-ico"
        rel="shortcut icon"
      />
      <link
        href="https://assets.hover.to/favicons/hover_fav_16.png"
        id="favicon-16"
        rel="icon"
        sizes="16x16"
        type="image/png"
      />
      <link
        href="https://assets.hover.to/favicons/hover_fav_32.png"
        id="favicon-32"
        rel="icon"
        sizes="32x32"
        type="image/png"
      />
      <link
        href="https://assets.hover.to/favicons/hover_fav_64.png"
        id="favicon-64"
        rel="icon"
        sizes="64x64"
        type="image/png"
      />
    </Helmet>
  );
};

export type MetaProps = {
  imageUrl?: string;
  description?: string;
  title?: string;
};

export const Meta = ({ imageUrl, description, title }: MetaProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const url = useLocation();

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta content="IE=edge" httpEquiv="X-UA-Compatible" />
        <meta
          content="width=device-width, initial-scale=1, viewport-fit=cover"
          name="viewport"
        />
        <meta content={description || t('description')} name="description" />
        <meta
          content={description || t('description')}
          property="og:description"
        />
        {title && <meta content={title} property="og:title" />}
        {imageUrl && <meta content={imageUrl} property="og:image" />}
        <link crossOrigin="" href="https://assets.hover.to" rel="preconnect" />
        <link crossOrigin="" href="https://graph.hover.to" rel="preconnect" />
        <meta content={url.href} property="og:url" />
      </Helmet>
      <Favicon />
    </>
  );
};
